import React, { useEffect, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import { IMenu } from '../types';
import Loading from './Loading';
import RestaurantMenuIcon from '@material-ui/icons/RestaurantMenu';
import firebase from 'firebase/app';

interface IParsedItem {
  name: string;
  description: string;
  price: number;
}

interface IParsedMenu {
  [key: string]: {
    items: IParsedItem[];
    description?: string;
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexFlow: 'column nowrap',
      width: '100%',
      marginBottom: 50,
    },
    menuCategory: {
      textAlign: 'center',
      margin: 20,
      boxSizing: 'border-box',
      maxWidth: 960,
      width: '100%',
      padding: '50px 100px 100px',
      borderRadius: 10,
      background: 'white',
      boxShadow: '1px 1px 10px #e0e0e0',
      border: '1px dashed #1b252d',
      [theme.breakpoints.down('sm')]: {
        padding: '20px 10px 10px',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        margin: 0,
      },
    },
    categoryText: {
      fontSize: 32,
      fontFamily: 'Times',
      textTransform: 'uppercase',
      letterSpacing: '5px',
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        letterSpacing: 3,
        fontSize: 26,
      },
    },
    categoryDesc: {
      margin: 0,
      marginBottom: 40,
    },
    itemWrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      flexFlow: 'column nowrap',
      position: 'relative',
    },
    itemText: {
      textAlign: 'left',
      fontFamily: 'Raleway, sans-serif',
      fontSize: 20,
      margin: 0,
      [theme.breakpoints.down('sm')]: {
        fontSize: 18,
      },
    },
    itemPrice: {
      textAlign: 'left',
      fontFamily: 'sans-serif',
      fontSize: 20,
      margin: 0,
      marginLeft: 20,
      [theme.breakpoints.down('sm')]: {
        fontSize: 18,
      },
    },
    itemDesc: {
      color: 'crimson',
      fontStyle: 'italic',
      textAlign: 'left',
      fontFamily: 'Raleway, sans-serif',
      fontSize: 16,
      margin: 0,
      marginTop: 5,
      [theme.breakpoints.down('sm')]: {
        fontSize: 14,
      },
    },
    menuItem: {
      boxSizing: 'border-box',
      background: '#F8F8F8',
      padding: '15px 30px',
      margin: '0 auto 5px',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: 100,
      boxShadow: '1px 1px 1px #e0e0e0',
      [theme.breakpoints.down('sm')]: {
        boxShadow: 'none',
        borderRadius: 0,
        padding: '15px 10px',
        margin: 0,
      },
    },
    pageHeading: {
      fontSize: 50,
      margin: 0,
      marginTop: 50,
      marginBottom: 20,
      fontFamily: 'Raleway, sans-serif',
      [theme.breakpoints.down('sm')]: {
        fontSize: 30,
      },
    },
    pageText: {
      maxWidth: 960,
      fontSize: 24,
      lineHeight: 1.5,
      margin: 0,
      marginBottom: 20,
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        fontSize: 18,
      },
    },
    topWrapper: {
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 30,
      [theme.breakpoints.down('sm')]: {
        padding: 10,
      },
    },
    allergensText: {
      fontSize: 18,
      maxWidth: 960,
      lineHeight: 1.5,
      margin: 0,
      textAlign: 'center',
      color: 'crimson',
      fontStyle: 'italic',
      marginTop: 40,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
      },
    },
  })
);

interface IOwnProps {
  firebase: typeof firebase;
}

export const Menu: React.FC<IOwnProps> = ({ firebase }) => {
  const classes = useStyles();

  const fetchMenu = firebase.functions().httpsCallable('fetchMenu');

  const [parsedMenu, setParsedMenu] = useState<IParsedMenu>();
  const [menu, setMenu] = useState<IMenu | null>(null);

  useEffect(() => {
    const getMenu = async () => {
      try {
        const response = await fetchMenu();
        setMenu(response.data);
      } catch (error) {
        setMenu(null);
      }
    };

    getMenu();
  }, []);

  const parseMenu = (menu: IMenu) => {
    const categories = menu.categories || [];

    const parsedCategories = categories.reduce((acc: IParsedMenu, current) => {
      const parsedItems =
        current.items?.reduce((accItems: IParsedItem[], currentItem) => {
          const item = {
            name: currentItem.name,
            description: currentItem.description,
            price: currentItem.price,
          };

          accItems.push(item);
          return accItems;
        }, []) || [];

      acc[current.name] = {
        items: parsedItems,
        description: current.description,
      };

      return acc;
    }, {});

    setParsedMenu(parsedCategories);
  };

  useEffect(() => {
    if (menu) {
      parseMenu(menu);
    }
  }, [menu]);

  return (
    <div className={classes.menuWrapper}>
      <div className={classes.topWrapper}>
        <h1 className={classes.pageHeading}>Menu</h1>
        <p className={classes.pageText}>
          We welcome you to indulge in our mouth watering dishes as a sit in or
          takeout option, offering breakfast, lunch, dinner and coffee for you
          to enjoy.
        </p>
        <p className={classes.pageText}>
          Please see our menu below or click to order online now.
        </p>
        <Button
          size='large'
          variant='outlined'
          startIcon={<RestaurantMenuIcon />}
          data-glf-cuid='fd3cddaa-823f-4a56-a896-5e9d4513963a'
          data-glf-ruid='31e232a9-0527-4d7f-94d2-324bc669aced'
        >
          Order Online
        </Button>
        <p className={classes.allergensText}>
          Please leave a note or call us with regards to you dietary
          requirements when placing your order. All of our chicken dishes are
          cooked with Halal chicken. We use Sesame oil.
        </p>
      </div>
      {parsedMenu ? (
        Object.keys(parsedMenu).map((item, index) => {
          return (
            <div key={index} className={classes.menuCategory}>
              <h1 className={classes.categoryText}>{item}</h1>
              <p className={classes.categoryDesc}>
                {parsedMenu[item].description}
              </p>
              {parsedMenu[item].items.map((food, i) => (
                <div key={i} className={classes.menuItem}>
                  <div className={classes.itemWrapper}>
                    <h2 className={classes.itemText}>{food.name}</h2>
                    {food.description && (
                      <p className={classes.itemDesc}>{food.description}</p>
                    )}
                  </div>
                  <h2 className={classes.itemPrice}>
                    £{food.price.toFixed(2)}
                  </h2>
                </div>
              ))}
            </div>
          );
        })
      ) : (
        <Loading />
      )}
      <Button
        size='large'
        variant='outlined'
        style={{ marginTop: 30 }}
        startIcon={<RestaurantMenuIcon />}
        data-glf-cuid='fd3cddaa-823f-4a56-a896-5e9d4513963a'
        data-glf-ruid='31e232a9-0527-4d7f-94d2-324bc669aced'
      >
        Order Online
      </Button>
    </div>
  );
};

export default Menu;
